import {
  AddressingType,
  ApartmentDTO,
  ApartmentInvitationDTO,
  ApartmentInvitationStatusEnum,
  BuildingDTO,
  EntrancePlantDetailDTO,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { loadRisersFloorsApartments } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';

import { processCsvDataMassiveInvitation } from '@/core/utils/utils';

interface EmailInput {
  email: string;
  isEnable: boolean;
}

@Options({
  name: 'DialogMassiveInviteResidents',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.initInvitations();
      }
    },
  },
  components: {},
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogMassiveInviteResidents extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  isVisible!: boolean;
  isDialogVisible = false;
  invites: ApartmentInvitationDTO[] = [];
  emailInput: { [key: string]: EmailInput } = {};

  AddressingType = AddressingType;

  showImportSection: boolean = false;

  buildingStructure(building: BuildingDTO) {
    return loadRisersFloorsApartments(building);
  }

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  handleHideDialog() {
    this.showImportSection = false;
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  triggerFilePicker() {
    const input = document.getElementById('fileInput') as HTMLInputElement;
    input.value = '';
    input.click();
  }

  importedData: ApartmentInvitationDTO[] = [];

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.$store.dispatch('SET_APP_LOADING', true);
    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const text = e.target?.result;
      this.$store.dispatch('SET_APP_LOADING', false);
      if (typeof text !== 'string') {
        return;
      } else {
        const data = await processCsvDataMassiveInvitation(
          text,
          this.currentEntrancePlant
        );
        if (data.length === 0) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('importFile.error'),
            closable: true,
            life: 6000,
          });
        }

        this.importedData = data;

        this.invites.push(...this.importedData);
        this.initInvitations();

        this.showImportSection = false;
      }
    };

    reader.onerror = (e) => {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('importFile.error'),
        closable: true,
        life: 6000,
      });
    };
    reader.readAsText(file);
  }

  tabBuildingLabel(building: BuildingDTO) {
    const apartCount = building.apartments
      ? `(${building.apartments.length})`
      : '';
    return `${building.name} ${apartCount}`;
  }

  apartmentInputLabel(apartment: ApartmentDTO) {
    const baseLabel = `${apartment.name}`;
    if (apartment.labels && apartment.labels?.length > 0) {
      return `${baseLabel} - ${
        apartment.labels[0].isPrivate
          ? apartment.labels[0].surname + ' ' + apartment.labels[0].name
          : apartment.labels[0].companyName
      } `;
    } else {
      return baseLabel;
    }
  }

  initInvitations() {
    if (this.currentEntrancePlant.apartmentInvitations)
      this.invites = [...this.currentEntrancePlant.apartmentInvitations];
    if (this.importedData) {
      this.invites.push(...this.importedData);
    }

    this.currentEntrancePlant.buildings?.forEach((building) =>
      building.apartments?.forEach((apartm) => {
        if (
          !this.invites.find(
            (invite) => invite.apartmentId === apartm.apartmentId
          )
        ) {
          this.invites.push(
            new ApartmentInvitationDTO({
              buildingId: apartm.buildingId,
              apartmentId: apartm.apartmentId,
              floorId: apartm.floorId,
              email: '',
              status: ApartmentInvitationStatusEnum.Pending,
            })
          );
        }
      })
    );

    for (const invite of this.invites) {
      if (invite.apartmentId !== undefined && invite.email !== undefined) {
        this.emailInput[invite.apartmentId] = {
          email: invite.email,
          isEnable: invite.email && !this.isImported(invite) ? false : true,
        };
      }
    }
  }

  disableInput(apartmentId: string) {
    return !this.emailInput[apartmentId]?.isEnable;
  }

  inPending(apartmentId: string) {
    if (this.invites.find((invite) => invite.apartmentId === apartmentId)) {
      return (
        this.invites.find((invite) => invite.apartmentId === apartmentId)
          ?.status === ApartmentInvitationStatusEnum.Pending
      );
    }
  }

  async handleButtonSubmitAction() {
    this.handleHideDialog();

    await this.sendInvites();
  }

  isImported(invite: ApartmentInvitationDTO) {
    return (
      this.importedData.find((i) => i.apartmentId === invite.apartmentId) !==
      undefined
    );
  }

  async handleImportClick() {
    this.showImportSection = true;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const csv = await plantsClient.invitationCsv(
        this.currentEntrancePlant.desktopId!,
        undefined
      );

      if (csv.data) {
        const url = window.URL.createObjectURL(new Blob([csv.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          this.currentEntrancePlant.name + '-invitation' + '.csv'
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode!.removeChild(link);
      }
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async sendInvites() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      const newInvites: ApartmentInvitationDTO[] = [];

      this.invites.forEach((invite) => {
        if (invite.apartmentId && this.emailInput[invite.apartmentId].isEnable)
          newInvites.push(
            new ApartmentInvitationDTO({
              apartmentId: invite.apartmentId,
              buildingId: invite.buildingId,
              floorId: invite.floorId,
              email: this.emailInput[invite.apartmentId].email,
              status: ApartmentInvitationStatusEnum.Pending,
            })
          );
      });

      await plantsClient.massiveinvitation(
        this.currentEntrancePlant.desktopId,
        newInvites
      );

      await this.$nextTick(async () => {
        this.$emit('update-list');
        this.$toast.add({
          severity: 'success',
          summary: this.$t('AppResourceEP.InvitationsSent'),
          closable: false,
          life: 3000,
        });
      });
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
